import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { categories, comics } from '@/mocks/comics';

type FilterKey = 'all' | 'hot' | 'new' | 'finished';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState<FilterKey>('all');

  const currentCategory = categories.find((c) => c.slug === slug);

  const filteredComics = useMemo(() => {
    let result = comics.filter((c) => c.category === slug);
    switch (activeFilter) {
      case 'hot':
        result = [...result].sort((a, b) => b.views - a.views);
        break;
      case 'new':
        result = [...result].sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
        break;
      case 'finished':
        result = result.filter((c) => c.status === '完结');
        break;
    }
    return result;
  }, [slug, activeFilter]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveFilter('all');
  }, [slug]);

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <p className="text-lg text-foreground-700 mb-4">{t('category.empty')}</p>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">{t('category.backToHome')}</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="w-full px-4 md:px-8 py-8 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={[{ label: currentCategory.name }]} />

            <div className="mt-6 mb-2">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {currentCategory.name}漫画
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                {currentCategory.description}
              </p>
            </div>

            {/* Filter tabs */}
            <div className="flex items-center gap-2 mt-6 mb-8 overflow-x-auto scrollbar-hide">
              {([
                { key: 'all' as FilterKey, label: t('category.filterAll') },
                { key: 'hot' as FilterKey, label: t('category.filterHot') },
                { key: 'new' as FilterKey, label: t('category.filterNew') },
                { key: 'finished' as FilterKey, label: t('category.filterFinished') },
              ]).map((f) => (
                <button
                  key={f.key}
                  onClick={() => setActiveFilter(f.key)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeFilter === f.key
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} {...comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-500">{t('category.empty')}</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${currentCategory.name}漫画 - 哔咔动漫`,
            description: `${currentCategory.name}漫画精选，哔咔动漫免费下拉式在线阅读。`,
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: "https://bikadongman.com/" },
                { "@type": "ListItem", position: 2, name: `${currentCategory.name}漫画`, item: `https://bikadongman.com/category/${slug}` },
              ],
            },
          }),
        }}
      />
    </>
  );
}